import { catalog } from "../i18n.js";

/** A number as Indonesian writes it: a decimal comma. */
const decimalComma = (value: string | number): string => String(value).replace(".", ",");

/** The page around the chat (App.tsx): its notices, the chat's header, the empty page and the popups' names. */
export const APP = catalog(
  {
    cannotReachBox: (detail: string) => `Cannot reach the box: ${detail}`,
    couldNotReadChat: (detail: string) => `Could not read the chat: ${detail}`,
    couldNotReadEarlier: (detail: string) => `Could not read earlier messages: ${detail}`,
    couldNotSaveSidebar: (detail: string) => `Could not save the sidebar: ${detail}`,
    pushNoPlugin: "Notifications are off: this build of the app has no push plugin.",
    pushNoFirebase: "Notifications are off: this build of the app has no Firebase project.",
    pushRegistered: (device: string) => `This phone (${device}) now gets notifications.`,
    pushNotRegistered: (detail: string) => `This phone could not be registered for notifications: ${detail}`,
    notificationsBlockedNotice: "The browser is blocking notifications for this site. Allow them in its site settings (the icon by the address), then press the bell again.",

    deleteBotTitle: (name: string) => `Delete the bot ${name}?`,
    deleteGroupTitle: (name: string) => `Delete the group ${name}?`,
    deleteBotMessage: "The bot and its whole conversation go, with its routines, skills and memory. This cannot be undone.",
    deleteGroupMessage: "The group and its whole conversation go. Its members stay.",
    delete: "Delete",

    waitingForApproval: "Waiting for your approval",
    typing: "Typing…",
    working: "Working",
    backgroundTask: "Background task running",
    members: (count: number) => `${count} members`,

    welcome: "Pick a bot, or press + to make one. It says hello on its own.",
    backToBots: "Back to bots",
    back: "Back",
    streamTitle: (state: string) => `event stream: ${state}`,
    streamLabel: (state: string) => `event stream ${state}`,
    streamConnecting: "connecting",
    streamOpen: "open",
    streamReconnecting: "reconnecting",
    streamClosed: "closed",
    notificationsBlockedLabel: "Notifications blocked; ask again",
    notificationsBlockedTitle: "Blocked by the browser; press to ask again",
    notificationsBlocked: "Notifications blocked",
    enableNotifications: "Enable notifications",
    enableNotificationsTitle: "Be told when a bot replies or needs you while this tab is in the background",
    showSidePane: "Show the side pane",
    sidePaneTitle: "The bot's channels, routines and settings",
    dismiss: "Dismiss",
    sidebarWidth: "Sidebar width",
    sidePaneWidth: "Side pane width",
    computerOf: (name: string) => `${name}'s computer`,
    pressBackAgain: "Press Back again to exit",
    settings: "Settings",
    plugins: "Plugins",
    host: "Host",
    showSidebar: "Show the sidebar",
    showSidebarTitle: (keys: string) => `Show the sidebar (${keys})`,
  },
  {
    cannotReachBox: (detail: string) => `Tidak bisa terhubung ke box: ${detail}`,
    couldNotReadChat: (detail: string) => `Chat tidak bisa dibaca: ${detail}`,
    couldNotReadEarlier: (detail: string) => `Pesan sebelumnya tidak bisa dibaca: ${detail}`,
    couldNotSaveSidebar: (detail: string) => `Sidebar tidak bisa disimpan: ${detail}`,
    pushNoPlugin: "Notifikasi mati: versi aplikasi ini tidak punya plugin push.",
    pushNoFirebase: "Notifikasi mati: versi aplikasi ini tidak punya proyek Firebase.",
    pushRegistered: (device: string) => `Ponsel ini (${device}) sekarang menerima notifikasi.`,
    pushNotRegistered: (detail: string) => `Ponsel ini tidak bisa didaftarkan untuk notifikasi: ${detail}`,
    notificationsBlockedNotice: "Browser memblokir notifikasi untuk situs ini. Izinkan di pengaturan situsnya (ikon di sebelah alamat), lalu tekan lonceng lagi.",

    deleteBotTitle: (name: string) => `Hapus bot ${name}?`,
    deleteGroupTitle: (name: string) => `Hapus grup ${name}?`,
    deleteBotMessage: "Bot ini dan seluruh percakapannya akan hilang, beserta rutinitas, keahlian, dan memorinya. Ini tidak bisa dibatalkan.",
    deleteGroupMessage: "Grup ini dan seluruh percakapannya akan hilang. Anggotanya tetap ada.",
    delete: "Hapus",

    waitingForApproval: "Menunggu persetujuanmu",
    typing: "Mengetik…",
    working: "Bekerja",
    backgroundTask: "Tugas latar belakang berjalan",
    members: (count: number) => `${count} anggota`,

    welcome: "Pilih bot, atau tekan + untuk membuatnya. Bot akan menyapa duluan.",
    backToBots: "Kembali ke daftar bot",
    back: "Kembali",
    streamTitle: (state: string) => `aliran event: ${state}`,
    streamLabel: (state: string) => `aliran event ${state}`,
    streamConnecting: "menghubungkan",
    streamOpen: "terbuka",
    streamReconnecting: "menyambung ulang",
    streamClosed: "tertutup",
    notificationsBlockedLabel: "Notifikasi diblokir; minta lagi",
    notificationsBlockedTitle: "Diblokir oleh browser; tekan untuk minta lagi",
    notificationsBlocked: "Notifikasi diblokir",
    enableNotifications: "Aktifkan notifikasi",
    enableNotificationsTitle: "Dapat kabar saat bot membalas atau butuh kamu, selagi tab ini di latar belakang",
    showSidePane: "Tampilkan panel samping",
    sidePaneTitle: "Channel, rutinitas, dan pengaturan bot",
    dismiss: "Tutup",
    sidebarWidth: "Lebar sidebar",
    sidePaneWidth: "Lebar panel samping",
    computerOf: (name: string) => `Komputer ${name}`,
    pressBackAgain: "Tekan Kembali sekali lagi untuk keluar",
    settings: "Pengaturan",
    plugins: "Plugin",
    host: "Host",
    showSidebar: "Tampilkan sidebar",
    showSidebarTitle: (keys: string) => `Tampilkan sidebar (${keys})`,
  },
);

/** The page's small dialogs and handles: ConfirmDialog, NameDialog, Lightbox, Resizer. */
export const DIALOGS = catalog(
  {
    cancel: "Cancel",
    close: "Close",
    closeEsc: "Close (Esc)",
    image: "Image",
    imageFile: "image",
    savePicture: "Save the picture",
    resize: "Drag to resize; double-click to reset",
  },
  {
    cancel: "Batal",
    close: "Tutup",
    closeEsc: "Tutup (Esc)",
    image: "Gambar",
    imageFile: "gambar",
    savePicture: "Simpan gambar",
    resize: "Seret untuk mengubah ukuran; klik dua kali untuk mengembalikan",
  },
);

/** The browser's notifications (notifications.ts). */
export const NOTIFICATIONS = catalog(
  {
    needsYou: (agentName: string) => `${agentName} needs you`,
  },
  {
    needsYou: (agentName: string) => `${agentName} butuh kamu`,
  },
);

/** The plan's usage (account.ts); the amounts come already written, to the tenth. */
export const ACCOUNT = catalog(
  {
    creditsUsed: (spent: string, limit: string) => `${spent} of ${limit} credits`,
  },
  {
    creditsUsed: (spent: string, limit: string) => `${decimalComma(spent)} dari ${decimalComma(limit)} kredit`,
  },
);
